import React from 'react'
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Grid, CircularProgress, Typography } from '@material-ui/core';
import useStyles from './style';
import TopHeaderViewer from './components/TopHeader';
import OverviewCardViewer from './components/OverviewCard';
import ActionBarViewer from './components/ActionBar';
import CardInfoViewer from './components/CardInfo';
import TabsViewer from './components/Tabs';
import BlockRenderer from './components/BlockRenderer';
import FooterViewer from './components/FooterViewer';
import ActivityTimeline from './components/ActivityTimeline';
import UnsavedChangesDialog from '../UnsavedChangesDialog';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';
import { toast } from 'react-toastify';
import { generateId, clearCurrentChartReduxStore, chatRedirect, getAllDataElementsFromBlocks, conditionMatches, getMatchingCondition, computeCalculatedFieldValue, getRequiredFieldsValidationErrors, getTierDateValidationErrors, normalizeDatesInObject, formatDealReviewHistoryTimePST, getTierControlFieldFromBlocks, getTierFieldsToClearOnTopTierChange, getInitialDefaultsFromBlocks, serializeDealState, getChangedFieldsVsPersisted, omitDocumentIdentityFields, isApiFetchSelectElement, getStatusTransitionForFieldChange } from './utils';
import { getNegotiationStatusAutoUpdatePayload, resolveNegotiationConfigFromBlocks, TOP_TIER_REQUIRED_FIELD } from './utils/negotiationStatusRules';
import { getTrackerThresholdConfigFromBlocks } from './config/resolveTrackerConfig';
import { getBelowTrackerThresholdError } from './utils/trackerThreshold';

const TrackerViewerView = (props) => {
  const { data, formData, appId, orgId } = props;
  const { chartData, chartUniqueId } = data || {};
  const { tabs, overview, actionBar, uploadCard, topBar, selectedDataModel, listPages } = chartData || {};
  const currentDocumentId = props?.match?.params?.id;
  const [json, setJson] = useState({});
  const [blockState, setBlockState] = useState(json)
  const [currentDataModel, setCurrentDataModel] = useState({})
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [belowThresholdError, setBelowThresholdError] = useState(null)
  const [auditTrailDrawerOpen, setAuditTrailDrawerOpen] = useState(false)
  const [auditTrail, setAuditTrail] = useState([])
  const [activityData, setActivityData] = useState(null)
  const [overriddenFields, setOverriddenFields] = useState(new Set())
  const classes = useStyles();
  const [activeTabMain, setActiveTabMain] = useState(Object.keys(tabs?.labels || {})[0] || 'dealReviewDetails');

  /** Last persisted deal snapshot (load / successful save) — compared to blockState for unsaved edits */
  const [persistedDealSerialized, setPersistedDealSerialized] = useState(null);

  /**
   * Activities tab only when isReset (matches ActivityTimeline panel).
   * Chart JSON may use any property key (e.g. `activities`); TabsViewer matches on `tab.name === 'activitiesDetails'`.
   */
  const tabsForHeader = useMemo(() => {
    if (!tabs || typeof tabs !== 'object') return tabs;
    const { enable: tabsRootEnable, labels = {}, ...tabDefs } = tabs;
    const next = {};
    for (const [tabKey, tabConfig] of Object.entries(tabDefs)) {
      if (!tabConfig || typeof tabConfig !== 'object') {
        next[tabKey] = tabConfig;
        continue;
      }
      const isActivitiesTab =
        tabConfig.name === 'activitiesDetails' || tabKey === 'activitiesDetails';
      if (isActivitiesTab) {
        const allowedByConfig = tabConfig.enable !== false;
        next[tabKey] = {
          ...tabConfig,
          enable: allowedByConfig && blockState?.isReset === true,
        };
      } else {
        next[tabKey] = tabConfig;
      }
    }
    return {
      ...tabs,
      ...next,
    };
  }, [tabs, blockState?.isReset]);

  const dealHasUnsavedChanges = useMemo(() => {
    if (persistedDealSerialized == null) return false;
    return serializeDealState(blockState) !== persistedDealSerialized;
  }, [blockState, persistedDealSerialized]);

  const {
    isPromptOpen: isUnsavedChangesPromptOpen,
    confirmLeave: confirmLeaveUnsavedChanges,
    cancelLeave: stayOnDealReviewPage,
  } = useUnsavedChangesGuard({ hasUnsavedChanges: dealHasUnsavedChanges, history: props.history });

  const blocks = tabs?.dealReviewDetails?.blocks;
  const allDataElements = useMemo(() => {
    return getAllDataElementsFromBlocks(blocks);
  }, [blocks]);

  const negotiationConfig = useMemo(
    () => resolveNegotiationConfigFromBlocks(blocks),
    [blocks],
  );

  const trackerThresholdConfig = useMemo(
    () => getTrackerThresholdConfigFromBlocks(blocks),
    [blocks],
  );

  const hasDeriveRules = Boolean(negotiationConfig?.autoUpdate?.rules?.length);

  useEffect(() => {
    if (currentDocumentId) {
      fetchSingleDocument()
    }
    if (!currentDocumentId) {
      const tabDefaults = tabs?.dealReviewDetails?.initialDefaults || {};
      const initialDefaults = getInitialDefaultsFromBlocks(blocks, tabDefaults);
      const { dealIdPrefix, ...fieldDefaults } = initialDefaults;
      const initialId = generateId(dealIdPrefix || 'Deal Review');
      setBlockState({
        dealReviewId: initialId
      });
      Object.entries(fieldDefaults).forEach(([key, value]) => {
        if (value !== undefined) onChange(key, value);
      });
      // Baseline for new-record dirty tracking, captured once the defaults above have
      // settled (functional updaters run after the onChange calls queued them).
      setBlockState(prev => {
        setPersistedDealSerialized(serializeDealState(prev));
        return prev;
      });
    }
    fetchDataModel()
    return (() => {
      clearCurrentChartReduxStore(props, [
        props.data.chartUniqueId + '_getsingledocument',
        props.data.chartUniqueId + '_audit_Trails_Documents',
        props.data.chartUniqueId + '_update',
        props.data.chartUniqueId + '_save',
        props.data.chartUniqueId + '_deal_review_history',
      ])
    })
  }, [])

  // Helper function to check if a field is a calculated field with override enabled
  const isCalculatedFieldWithOverride = useCallback((fieldName, currentState) => {
    if (!allDataElements?.length) return false;
    for (const element of allDataElements) {
      const conditionArray = element.condition;
      if (!conditionArray?.length) continue;
      const matchingCond = getMatchingCondition(conditionArray, currentState);
      if (!matchingCond?.calculatedFields) continue;
      const config = matchingCond.calculatedFields[fieldName];
      if (config && config.isOverride === true) {
        return true;
      }
    }
    return false;
  }, [allDataElements]);

  // Helper function to compute all calculated fields based on current state
  const computeAllCalculatedFields = useCallback((currentState, overriddenFieldsSet = new Set()) => {
    if (!allDataElements?.length) return currentState;

    let updatedState = { ...currentState };

    allDataElements.forEach(element => {
      const conditionArray = element.condition;
      if (!conditionArray?.length) return;

      const matchingCond = getMatchingCondition(conditionArray, updatedState);
      if (!matchingCond?.calculatedFields) return;

      // Remove fields that should be removed
      if (Array.isArray(matchingCond.removedFields)) {
        matchingCond.removedFields.forEach((fieldName) => {
          if (fieldName in updatedState) {
            delete updatedState[fieldName];
          }
        });
      }

      // Compute calculated fields in order
      const fieldOrder = Array.isArray(matchingCond.calculatedFieldsOrder)
        ? matchingCond.calculatedFieldsOrder
        : Object.keys(matchingCond.calculatedFields);

      fieldOrder.forEach((fieldName) => {
        // Skip if field is overridden
        if (overriddenFieldsSet.has(fieldName)) return;
        const config = matchingCond.calculatedFields[fieldName];
        if (config == null) return;
        const computed = computeCalculatedFieldValue(updatedState, config);
        if (computed != null) {
          updatedState[fieldName] = computed;
        }
      });
    });

    return updatedState;
  }, [allDataElements]);

  useEffect(() => {
    if (props.formData?.document[chartUniqueId + '_getsingledocument']) {
      let obj = props.formData?.document[chartUniqueId + '_getsingledocument'] || {}
      const normalized = normalizeDatesInObject(obj)
      setJson(normalized);

      // Check which calculated fields with isOverride:true already have values from API
      // These should be marked as overridden to prevent recalculation
      const initialOverriddenFields = new Set();
      // const initialState = {
      //   ...normalized,
      //   decisionHistory: Array.isArray(normalized.decisionHistory) ? normalized.decisionHistory : []
      // };

      const initialState = {
        ...normalized
      };

      // Check all fields in the loaded document
      Object.keys(normalized).forEach(fieldName => {
        // Skip decisionHistory as it's not a calculated field
        if (fieldName === 'decisionHistory') return;

        // If field has a value and is a calculated field with override enabled, mark it as overridden
        if (normalized[fieldName] != null && normalized[fieldName] !== '') {
          if (isCalculatedFieldWithOverride(fieldName, initialState)) {
            initialOverriddenFields.add(fieldName);
          }
        }
      });

      // Set overridden fields
      setOverriddenFields(initialOverriddenFields);

      // Compute calculated fields on initial load (skip overridden fields)
      const stateWithCalculatedFields = computeAllCalculatedFields(initialState, initialOverriddenFields);

      setBlockState((prev) => {
        const decisionHistory = Array.isArray(stateWithCalculatedFields.decisionHistory)
          ? stateWithCalculatedFields.decisionHistory
          : (Array.isArray(normalized.decisionHistory)
            ? normalized.decisionHistory
            : (prev?.decisionHistory || []));

        const nextBlockState = {
          ...stateWithCalculatedFields,
          decisionHistory,
        };
        setPersistedDealSerialized(serializeDealState(nextBlockState));
        return nextBlockState;
      });
    }
  }, [props.formData?.document[chartUniqueId + '_getsingledocument'], computeAllCalculatedFields, isCalculatedFieldWithOverride])

  useEffect(() => {
    if (props.formData?.auditTrails?.[chartUniqueId + '_audit_Trails_Documents']) {
      let obj = props.formData?.auditTrails[chartUniqueId + '_audit_Trails_Documents'] || {}
      setAuditTrail(obj)
    }
  }, [props.formData?.auditTrails?.[chartUniqueId + '_audit_Trails_Documents']])

  useEffect(() => {
    if (activeTabMain === 'activitiesDetails' && blockState?.isReset !== true) {
      setActiveTabMain('dealReviewDetails');
    }
  }, [activeTabMain, blockState?.isReset])

  useEffect(() => {
    if (activeTabMain !== 'activitiesDetails') return;
    if (blockState?.isReset !== true) return;
    const docId = currentDocumentId || blockState?._id || blockState?.id;
    if (!docId) return;
    const activitiesCfg = tabs?.activities;
    if (!activitiesCfg?.dataModelId || !activitiesCfg?.subDataModelId) return;
    if (!props.getDealReviewHistory) return;

    props.getDealReviewHistory({
      appId: props.match.params.appid,
      dataModelId: activitiesCfg.dataModelId,
      subDataModelId: activitiesCfg.subDataModelId,
      documentId: String(docId),
      dataUniqueId: `${chartUniqueId}_deal_review_history`,
      payload: {
        ...(tabs?.activities?.dealReviewHistoryFields ? { fields: tabs?.activities?.dealReviewHistoryFields.map(field => field.value).join(',') } : {}),
      }
    });
  }, [
    activeTabMain,
    blockState?.isReset,
    blockState?._id,
    blockState?.id,
    currentDocumentId,
    chartUniqueId,
    tabs?.activities?.dataModelId,
    tabs?.activities?.subDataModelId,
    props.match.params.appid,
    props.getDealReviewHistory,
  ]);

  useEffect(() => {
    const raw = props.formData?.document?.[`${chartUniqueId}_deal_review_history`];
    if (raw == null) return;

    const inner = raw?.data;
    if (inner == null) return;

    /** ActivityTimeline expects { data: ActivityEntry[] }; Redux may store full API body or only the list */
    let timeline = null;
    if (inner && typeof inner === 'object' && !Array.isArray(inner) && Array.isArray(inner.data)) {
      timeline = { data: inner.data };
    } else if (Array.isArray(inner)) {
      timeline = { data: inner };
    }
    if (timeline) {
      setActivityData(timeline);
    }
  }, [props.formData?.document?.[`${chartUniqueId}_deal_review_history`], chartUniqueId]);

  useEffect(() => {
    const saveDealReviewPayload = props.formData?.document?.[`${chartUniqueId}_save_deal_review_history`];
    if (!saveDealReviewPayload) return;

    const msg =
      saveDealReviewPayload?.message ||
      saveDealReviewPayload?.data?.message ||
      'Deal Reset Completed';
    if (saveDealReviewPayload?.error != null && saveDealReviewPayload?.error !== '') {
      toast.error(typeof saveDealReviewPayload.error === 'string' ? saveDealReviewPayload.error : 'Reset failed');
    } else {
      toast.success(msg);
    }

    clearCurrentChartReduxStore(props, [
      props.data.chartUniqueId + '_getsingledocument',
      props.data.chartUniqueId + '_save_deal_review_history',
    ]);
    fetchSingleDocument({ force: true });
  }, [props.formData?.document?.[`${chartUniqueId}_save_deal_review_history`], chartUniqueId]);

  useEffect(() => {
    const saveResponse = props.formData?.document?.[chartUniqueId + '_save'];
    if (saveResponse && saveResponse?._id) {
      let res = saveResponse?._id;
      if (res) {
        toast.success(res.message || 'Document Created Successfully')
        setPersistedDealSerialized(serializeDealState(blockState))
        props.clearReduxDataOfCurrentComponent({
          key: 'document',
          subKey: [`${chartUniqueId}_save`]
        })
        chatRedirect(props.history, props.match.params.appid, listPages)
      }
    }
  }, [props.formData?.document?.[chartUniqueId + '_save']])

  useEffect(() => {
    if (currentDocumentId) {
      const updateResponse = props.formData?.document?.[chartUniqueId + '_update'];
      if (updateResponse && updateResponse?._id) {
        let res = updateResponse;
        if (res && Object.keys(res).length > 0) {
          toast.success(res.message || 'Document Updated Successfully')
          setBlockState(prev => {
            const merged = {
              ...normalizeDatesInObject(res),
              decisionHistory: Array.isArray(res.decisionHistory) && res.decisionHistory.length > 0
                ? res.decisionHistory
                : (prev?.decisionHistory || []),
            };
            setPersistedDealSerialized(serializeDealState(merged));
            return merged;
          })
          props.clearReduxDataOfCurrentComponent({
            key: 'document',
            subKey: [`${chartUniqueId}_audit_Trails_Documents`]
          })
          props.getAuditTrailsData({
            dataModelId: selectedDataModel,
            appId: props.match.params.appid,
            documentId: props.match.params.id,
            dataUniqueId: chartUniqueId + '_audit_Trails_Documents'
          })
          props.clearReduxDataOfCurrentComponent({
            key: 'document',
            subKey: [`${chartUniqueId}_update`]
          })
        }
      }
    }
  }, [props.formData?.document?.[chartUniqueId + '_update']])


  const fetchDataModel = () => {
    let payload = {
      appid: props.appId,
      orgId: props.orgId
    }
    if (selectedDataModel) {
      if (!formData?.dataModel?.[selectedDataModel]) {
        if (!formData?.dataModel?.[selectedDataModel] && !formData[`${selectedDataModel}_loading`]) {
          payload['dataModelId'] = selectedDataModel
          payload['dataUniqueId'] = selectedDataModel
          props.getDataModelById(payload)
        }
      } else {
        setCurrentDataModel(formData?.dataModel?.[selectedDataModel])
      }
    }
  }

  /** Pass `{ force: true }` after clearing `_getsingledocument` in Redux — props in the same tick still hold stale data, so the default guard would skip `getSingleOpportunity`. */
  const fetchSingleDocument = (options = {}) => {
    const force = options.force === true;
    const cached = props.formData?.document?.[chartUniqueId + '_getsingledocument'];
    if (!force && cached) {
      return;
    }
    if (!currentDocumentId) {
      return;
    }
    const payload = {
      appId: appId,
      orgId: orgId,
      dataModelId: selectedDataModel,
      opportunityId: currentDocumentId,
      dataUniqueId: chartUniqueId + '_getsingledocument',
    };
    props.getSingleOpportunity(payload);
  };

  const handleAuditTrailDrawerToggle = () => {
    const willOpen = !auditTrailDrawerOpen;
    setAuditTrailDrawerOpen(willOpen);

    if (willOpen && currentDocumentId) {
      props.getAuditTrailsData({
        dataModelId: selectedDataModel,
        appId: props.match.params.appid,
        daAuditTrailUnique: true,
        documentId: props.match.params.id,
        dataUniqueId: chartUniqueId + '_audit_Trails_Documents'
      })
    }
  }

  const handleExport = (id, item) => {
    let payload = {
      dataModelId: selectedDataModel,
      appid: props.match.params.appid,
      fields: actionBar?.ActionButton?.export?.exportdataElement?.map(i => i.value).join(","),
      ids: [id],
      downloadType: 'excel',
      dataUniqueId: props.data.chartUniqueId + '_' + currentDocumentId + '_download_single_document',
    }
    props.downloadSheet(payload);
  }

  // const onChange = (key, value) => {
  //   // let newBlockState = { ...blockState }
  //   // newBlockState[key] = value
  //   setBlockState(newBlockState);
  // }

  const onChange = useCallback((key, value) => {
    const el = allDataElements.find(
      (e) => e?.value === key && e?.isGreaterThanZero
    );

    if (el) {
      const numberValue = Number(value);

      if (value === "" || value === null || value === undefined) return;

      if (Number.isNaN(numberValue)) {
        toast.error("Please enter a valid number");
        return;
      }

      if (numberValue < 0) {
        toast.error("Deal value cannot be less than 0");
        return;
      }
    }

    setShowValidationErrors(false);
    setBelowThresholdError(null);

    setBlockState(prevBlockState => {
      setOverriddenFields(prevOverridden => {
        const isCalculatedWithOverride = isCalculatedFieldWithOverride(key, prevBlockState);
        if (isCalculatedWithOverride) {
          const newOverridden = new Set(prevOverridden);
          newOverridden.add(key);
          return newOverridden;
        }
        return prevOverridden;
      });

      let newBlockState = { ...prevBlockState };

      const changedElement = allDataElements.find(
        (el) => el?.value === key || el?.name === key
      );

      if (value === null || value === undefined) {
        newBlockState[key] = isApiFetchSelectElement(changedElement) ? null : '';
      } else {
        newBlockState[key] = value;
      }

      const controlField = getTierControlFieldFromBlocks(blocks);
      const previousTopTierValue = prevBlockState?.[controlField] ?? prevBlockState?.topTierRequired;

      const clearTierFieldsForTopTierChange = (previousValue, nextValue) => {
        const fieldsToClear = getTierFieldsToClearOnTopTierChange(
          previousValue,
          nextValue,
          allDataElements,
        );
        fieldsToClear.forEach((fieldName) => {
          delete newBlockState[fieldName];
          delete newBlockState[`${fieldName}History`];
        });
      };

      if (key === controlField) {
        clearTierFieldsForTopTierChange(previousTopTierValue, value);
      }

      allDataElements.forEach(element => {
        if (key === element.value && element.isHistory && element.showHistroy) {
          newBlockState[`${element.value}History`] = [...(newBlockState[`${element.value}History`] || []), {
            value: value || '',
            updatedAtPST: formatDealReviewHistoryTimePST(new Date()),
            updatedBy: props.members?.name || 'Unknown'
          }];
        }
      })

      // Process calculated fields for all relevant elements
      allDataElements.forEach(element => {
        const isRelevantElement = element.value === key || (Array.isArray(element.relatedFields) && element.relatedFields.includes(key));

        if (!isRelevantElement) return;

        // Handle removed fields when value is cleared
        if (value === '' || value === null || value === undefined) {
          if (Array.isArray(element.removedFields)) {
            element.removedFields.forEach((fieldName) => {
              if (fieldName in newBlockState) {
                delete newBlockState[fieldName];
                // Remove from overridden fields if field is deleted
                setOverriddenFields(prev => {
                  const updated = new Set(prev);
                  updated.delete(fieldName);
                  return updated;
                });
              }
            });
          }
        }

        const conditionArray = element.condition;
        if (!conditionArray?.length) return;

        const matchingCond = getMatchingCondition(conditionArray, newBlockState);
        if (!matchingCond) {
          if (Array.isArray(element.removedFields)) {
            element.removedFields.forEach((fieldName) => {
              if (fieldName in newBlockState) {
                if (fieldName === controlField) {
                  clearTierFieldsForTopTierChange(previousTopTierValue, null);
                }
                delete newBlockState[fieldName];
                setOverriddenFields(prev => {
                  const updated = new Set(prev);
                  updated.delete(fieldName);
                  return updated;
                });
              }
            });
          }
          return;
        }

        if (!matchingCond?.calculatedFields) return;

        // Remove fields that should be removed based on condition
        if (Array.isArray(matchingCond.removedFields)) {
          matchingCond.removedFields.forEach((fieldName) => {
            if (fieldName in newBlockState) {
              delete newBlockState[fieldName];
              // Remove from overridden fields if field is deleted
              setOverriddenFields(prev => {
                const updated = new Set(prev);
                updated.delete(fieldName);
                return updated;
              });
            }
          });
        }

        // Compute calculated fields in specified order
        const fieldOrder = Array.isArray(matchingCond.calculatedFieldsOrder)
          ? matchingCond.calculatedFieldsOrder
          : Object.keys(matchingCond.calculatedFields);

        fieldOrder.forEach((fieldName) => {
          // Skip if field is overridden
          if (overriddenFields.has(fieldName)) return;
          const config = matchingCond.calculatedFields[fieldName];
          if (config == null) return;
          const computed = computeCalculatedFieldValue(newBlockState, config);
          if (computed != null) {
            newBlockState[fieldName] = computed;
          }
        });
      });

      const updatedTopTierValue = newBlockState[controlField] ?? newBlockState[TOP_TIER_REQUIRED_FIELD];
      if (updatedTopTierValue !== previousTopTierValue) {
        clearTierFieldsForTopTierChange(previousTopTierValue, updatedTopTierValue);
      }

      if (!hasDeriveRules) {
        const statusTransition = getStatusTransitionForFieldChange(
          key,
          newBlockState[key],
          prevBlockState,
          blocks,
          changedElement,
        );
        if (statusTransition) {
          if (statusTransition.clear || statusTransition.newStatus == null) {
            delete newBlockState[statusTransition.statusField];
          } else {
            newBlockState[statusTransition.statusField] = statusTransition.newStatus;
          }
        }
      }

      const negotiationStatusUpdate = getNegotiationStatusAutoUpdatePayload(
        key,
        newBlockState[key],
        newBlockState,
        negotiationConfig,
      );
      Object.assign(newBlockState, negotiationStatusUpdate);

      if (newBlockState[TOP_TIER_REQUIRED_FIELD] !== prevBlockState[TOP_TIER_REQUIRED_FIELD]) {
        Object.assign(
          newBlockState,
          getNegotiationStatusAutoUpdatePayload(
            TOP_TIER_REQUIRED_FIELD,
            newBlockState[TOP_TIER_REQUIRED_FIELD],
            newBlockState,
            negotiationConfig,
          ),
        );
      }

      return newBlockState;
    });
  }, [allDataElements, props.members?.name, isCalculatedFieldWithOverride, overriddenFields, blocks, negotiationConfig, hasDeriveRules]);

  const handleCheckboxChange = (value) => {
  }

  const saveData = () => {
    const blocks = tabs?.dealReviewDetails?.blocks;

    const thresholdError = getBelowTrackerThresholdError(blockState, trackerThresholdConfig);
    if (thresholdError) {
      setShowValidationErrors(true);
      setBelowThresholdError(thresholdError);
      return;
    }
    setBelowThresholdError(null);

    const { isValid, errors } = getRequiredFieldsValidationErrors(blocks, blockState, formData, selectedDataModel);
    if (!isValid) {
      setShowValidationErrors(true);
      const message = errors.length === 1
        ? `${errors[0].label} is required`
        : 'Please fill all required fields before saving';
      toast.error(message);
      return;
    }
    const { isValid: tierDateValid, errors: tierDateErrors, mode: tierDateMode } = getTierDateValidationErrors(blockState, blocks);
    if (!tierDateValid && tierDateMode === 'error') {
      setShowValidationErrors(true);
      const message = tierDateErrors.length === 1
        ? tierDateErrors[0].label
        : 'Please fix tier date validation errors before saving';
      toast.error(message);
      return;
    }
    let data = normalizeDatesInObject(Object.assign({}, blockState))
    let formDataPayload = {
      appId: props.match.params.appid,
      dataModelId: chartData?.selectedDataModel,
      payload: {
        ...data,
        status: 'Active'
      }
    }
    if (currentDocumentId) {
      let blockStateObject = JSON.parse(JSON.stringify(data))
      if (blockStateObject['_id']) {
        blockStateObject['id'] = blockStateObject['_id'];
        delete blockStateObject._id;
      }
      const patchPayload = getChangedFieldsVsPersisted(blockStateObject, persistedDealSerialized);
      let payloadData = {
        appId: props.match.params.appid,
        dataModelId: selectedDataModel,
        dataUniqueId: chartUniqueId + '_update',
        opportunityId: currentDocumentId,
        payload: omitDocumentIdentityFields(patchPayload),
      }
      props.updateOpportunity(payloadData);
    } else {
      if (Object.keys(data).length === 1) {
        toast.error('Please fill all the details before saving')
        return;
      }
      formDataPayload['dataUniqueId'] = chartUniqueId + '_save'
      props.createOpportunity(formDataPayload)
    }
  }

  const isDealReviewHistoryLoading = Boolean(
    formData?.[`${chartUniqueId}_deal_review_history_loading`],
  );

  return (
    <div className={classes.container}>
      <Grid container className={classes.headerGrid}>
        <Grid item xs={12} className={classes.headerRow}>
          <TopHeaderViewer {...props} blockState={blockState} isUpdated={currentDocumentId ? true : false} listPages={listPages} />
          <OverviewCardViewer {...props} src={overview?.src} data={overview} isUpdated={currentDocumentId ? true : false} />
          <ActionBarViewer {...props} isButtonEnable={actionBar?.ActionButton?.enable} isExport={actionBar?.ActionButton?.export?.enable} isAuditTrail={actionBar?.ActionButton?.auditTrail?.enable} handleAuditTrailDrawerToggle={handleAuditTrailDrawerToggle} auditTrailDrawerOpen={auditTrailDrawerOpen} auditTrail={auditTrail} chartUniqueId={chartUniqueId} label={actionBar?.actionHeader?.label} value={actionBar?.actionHeader?.value} blockState={blockState} exportLabel={actionBar?.ActionButton?.export?.label} auditTrailLabel={actionBar?.ActionButton?.auditTrail?.label} handleExport={handleExport} isUpdated={currentDocumentId ? true : false} currentDocumentId={currentDocumentId} />
          <CardInfoViewer {...props} data={uploadCard?.dataElement} blockState={blockState} />
          <TabsViewer {...props} tabs={tabsForHeader} setActiveTabMain={setActiveTabMain} activeTabMain={activeTabMain} />
          {
            activeTabMain === 'dealReviewDetails' && (
              <div className={classes.dealReviewDetailsParent}>
                <div className={classes.dealReviewDetails}>
                  <BlockRenderer blocks={tabs?.dealReviewDetails?.blocks} blockState={blockState} showValidationErrors={showValidationErrors} auditTrail={auditTrail} {...props} onChange={onChange}
                    handleCheckboxChange={handleCheckboxChange} selectedDataModel={selectedDataModel} overriddenFields={overriddenFields} dealHasUnsavedChanges={dealHasUnsavedChanges} />
                </div>
                <div className={classes.dealReviewFooter}>
                  <FooterViewer saveData={saveData} blockState={blockState} belowThresholdError={belowThresholdError} {...props} isUpdated={currentDocumentId ? true : false} listPages={listPages} />
                </div>
              </div>
            )
          }
          {
            activeTabMain === 'activitiesDetails' && blockState?.isReset === true && (
              <div style={{ padding: '0 4px', maxHeight: 520, overflowY: 'auto', minHeight: 200 }}>
                {isDealReviewHistoryLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 240,
                      padding: '32px 16px',
                      gap: 16,
                    }}
                  >
                    <CircularProgress size={40} thickness={3.6} color="primary" />
                    <Typography variant="body2" color="textSecondary" component="p" style={{ margin: 0 }}>
                      Loading activity history…
                    </Typography>
                  </div>
                ) : (
                  <ActivityTimeline
                    data={activityData}
                    historyFields={tabs?.activities?.dealReviewHistoryFields}
                  />
                )}
              </div>
            )
          }
        </Grid>
      </Grid>
      <UnsavedChangesDialog
        open={isUnsavedChangesPromptOpen}
        onStay={stayOnDealReviewPage}
        onLeave={confirmLeaveUnsavedChanges}
      />
    </div>
  )
}

export default TrackerViewerView